import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Share2, Check, MessageCircle, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, getCategories } from "@/lib/api";
import { buildWhatsAppUrl } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import { ProductCard } from "@/components/store/ProductCard";
import { Seo } from "@/components/Seo";
import { Button } from "@/components/ui/button";

// Catálogo digital compartilhável, organizado por categoria e sempre atualizado:
// /catalogo (tudo) · /catalogo?categoria=slug (uma categoria) · /colecao/:slug (seleção do painel)
export default function CatalogoPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const settings = useSettings();
  const [products, setProducts] = useState(null);
  const [categories, setCategories] = useState([]);  // raízes, na ordem do menu
  const [allCats, setAllCats] = useState([]);        // inclui subcategorias
  const [title, setTitle] = useState("Catálogo HG Móveis");
  const [copied, setCopied] = useState(false);
  const category = params.get("categoria") || "";

  useEffect(() => {
    getCategories().then((cats) => { setAllCats(cats); setCategories(cats.filter((c) => !c.parent_id)); }).catch(() => {});
  }, []);

  useEffect(() => {
    setProducts(null);
    if (slug) {
      api.get(`/collections/${slug}`).then((r) => {
        setProducts(r.data.products || []);
        setTitle(r.data.name);
      }).catch(() => setProducts([]));
    } else {
      // catálogo inteiro numa única requisição; as fotos carregam sob demanda (lazy)
      api.get("/products", { params: { limit: 200, ...(category ? { category } : {}) } }).then((r) => {
        setProducts(r.data.items || []);
        const catObj = allCats.find((c) => c.slug === category);
        const parent = catObj?.parent_id ? allCats.find((c) => c.id === catObj.parent_id) : null;
        const catName = catObj ? (parent ? `${parent.name} — ${catObj.name}` : catObj.name) : category;
        setTitle(category ? `Catálogo: ${catName}` : "Catálogo completo HG Móveis");
      }).catch(() => setProducts([]));
    }
  }, [slug, category, allCats]);

  // agrupa por categoria raiz, na ordem do menu
  const groups = useMemo(() => {
    if (!products) return [];
    if (slug) return [{ name: title, slug: "", items: products }];
    const byId = {};
    products.forEach((p) => {
      const own = allCats.find((c) => c.id === p.category_id);
      const root = own && own.parent_id ? allCats.find((c) => c.id === own.parent_id) : own;
      const key = root ? root.id : "outros";
      if (!byId[key]) byId[key] = { name: root ? root.name : "Outros", slug: root ? root.slug : "", items: [] };
      byId[key].items.push(p);
    });
    const ordered = categories.map((c) => byId[c.id]).filter(Boolean);
    if (byId["outros"]) ordered.push(byId["outros"]);
    return ordered;
  }, [products, categories, allCats, slug, title]);

  const shareUrl = window.location.href;
  const shareCategoryUrl = (s) => `${window.location.origin}/catalogo?categoria=${s}`;
  const copyLink = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(url);
      toast.success("Link copiado!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Não foi possível copiar. Copie o endereço da página.");
    }
  };

  return (
    <div className="container-store py-8 sm:py-12" data-testid="catalogo-page">
      <Seo
        title={`${title} | HG Móveis`}
        description="Catálogo digital da HG Móveis: móveis com preços reais, fotos e pagamento na entrega. Compartilhe pelo WhatsApp."
        canonical={slug ? `/colecao/${slug}` : "/catalogo"}
      />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight" data-testid="catalogo-title">{title}</h1>
          <p className="text-sm text-gray-500 mt-1">
            Preços, fotos e estoque sempre atualizados — quem abre o link vê as informações do momento.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-full" onClick={() => copyLink(shareUrl)} data-testid="catalogo-copy">
            {copied === shareUrl ? <Check className="w-4 h-4 text-green-600" /> : <Share2 className="w-4 h-4" />}
            {copied === shareUrl ? "Copiado!" : "Copiar link"}
          </Button>
          <a
            href={buildWhatsAppUrl(`Olha este catálogo da HG Móveis: ${shareUrl}`, settings.whatsapp_number)}
            target="_blank"
            rel="noreferrer"
          >
            <Button className="rounded-full bg-green-600 hover:bg-green-700" data-testid="catalogo-share-whatsapp">
              <MessageCircle className="w-4 h-4" /> Compartilhar
            </Button>
          </a>
        </div>
      </div>

      {products === null ? (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => <div key={i} className="animate-pulse bg-gray-100 rounded-xl h-64" />)}
        </div>
      ) : products.length === 0 ? (
        <p className="mt-10 text-center text-gray-500" data-testid="catalogo-empty">Nenhum produto nesta seleção no momento.</p>
      ) : (
        groups.map((g) => (
          <section key={g.name} className="mt-10 first:mt-8" data-testid={`catalogo-group-${g.slug || "colecao"}`}>
            {!slug && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h2 className="font-heading text-xl sm:text-2xl font-bold text-gray-900">{g.name}</h2>
                {g.slug && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyLink(shareCategoryUrl(g.slug))}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-brand transition-colors"
                      data-testid={`catalogo-share-${g.slug}`}
                    >
                      {copied === shareCategoryUrl(g.slug) ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Share2 className="w-3.5 h-3.5" />}
                      Compartilhar {g.name}
                    </button>
                    <Link to={`/categoria/${g.slug}`} className="inline-flex items-center gap-1 text-xs font-semibold text-brand hover:underline">
                      Ver no site <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                )}
              </div>
            )}
            <div className="mt-5 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-5" data-testid="catalogo-grid">
              {g.items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
